// [S-061a] Contract review hooks. Backed by the admin REST API:
//
//   POST /reviews                  → run a new review
//   GET  /reviews/:id              → fetch a previously-run review
//   POST /audit/attorney-override  → emit an AttorneyOverride audit event
//
// Reviews are cached in TanStack Query under [reviews, id]. Mutations
// invalidate the list to keep any future "recent reviews" surface in
// sync.
import { useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi, useApiStream } from "./api";

// Wire shape mirrors the @csai/core Review entity (see
// packages/core/src/entities/Review.ts). Kept inline so the web bundle
// doesn't pull in the core barrel just for type imports.
export type ReviewDeviationKind = "missing" | "added" | "substantive";
export type ReviewRiskLevel = "high" | "medium" | "low";

export type ReviewDeviation = {
  findingId: string;
  kind: ReviewDeviationKind;
  riskLevel: ReviewRiskLevel;
  /** [S-365] Section label from the LLM reviewer (e.g. "§5 Compensation"). */
  section?: string;
  contractClauseIndex: number | null;
  templateClauseIndex: number | null;
  similarity?: number;
  contractClause: string;
  templateClause: string;
  reason: string;
  /** [Phase 5] Clause type (lower_snake_case) classified by the LLM reviewer —
   *  pre-fills the negotiation Compare drawer. "" / absent on older reviews. */
  clauseType?: string;
  /** [S-373a] Concrete conforming clause text the reviewer suggests pasting —
   *  null/absent when no specific language applies. */
  suggestedReplacement?: string | null;
  /** [S-360b] Precedent-grounded negotiation context — connects this certified-
   *  standard finding to the studio's real precedents. null/absent when no
   *  comparable precedent bears on it (or synthesis wasn't wired). */
  precedentContext?: ReviewPrecedentContext | null;
};

// [S-360b] An LLM note grounding a finding in the studio's precedents, with the
// ids it cites (into Review.precedents) so the card can render openable chips.
export type ReviewPrecedentContext = {
  note: string;
  citedPrecedentIds: string[];
};

// [S-360/S-361] An L2 precedent surfaced alongside the certified-
// template deviations — a historical contract similar to the uploaded draft.
// Defaulted to [] server-side, so older cached reviews stay valid.
export type ReviewPrecedent = {
  id: string;
  /** Cosine similarity to the uploaded draft (0–1). */
  score: number;
  // Card-facing labels enriched from the precedent's structured contract (same
  // treatment as the Studio Agents reference cards). Absent on older reviews /
  // thin-metadata precedents — the panel falls back to the id.
  title?: string;
  subtitle?: string;
  summary?: string;
};

export type Review = {
  id: string;
  tenantId: string | null;
  contractTextHash: string;
  contractByteLength: number;
  dealType:
    | "feature_film"
    | "tv_series"
    | "streaming"
    | "documentary"
    | "commercial"
    | "short_form";
  guild: "SAG" | "DGA" | "WGA" | "IATSE" | "AFM" | "PGA";
  templateId: string | null;
  scopeAnchor: string;
  confidence: number;
  deviations: ReviewDeviation[];
  /** [S-361] L2 precedents similar to the uploaded draft, ranked by
   *  similarity. Optional for backward-compat with reviews cached before
   *  S-360 shipped; treat `undefined` as `[]`. */
  precedents?: ReviewPrecedent[];
  layersPresent: ("L1" | "L2")[];
  /** [S-766] `building` while a chat-started review runs in the background;
   *  absent on reviews from before the field existed (= complete). */
  status?: "building" | "complete" | "failed";
  buildError?: string | null;
  runByUid: string;
  createdAt: string;
};

export const REVIEWS_KEY = ["reviews"] as const;

export type RunReviewInput = {
  /** Uploaded contract — DOCX, PDF, TXT, or HTML. Server extracts
   *  plain text via mammoth/pdf-parse before running the review.
   *  Contracts are too long to paste; the textarea was removed. */
  file: File;
  dealType: Review["dealType"];
  guild: Review["guild"];
  templateId?: string;
};

export function useRunReview(): UseMutationResult<Review, Error, RunReviewInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => {
      const form = new FormData();
      form.append("file", input.file, input.file.name);
      form.append("dealType", input.dealType);
      form.append("guild", input.guild);
      if (input.templateId !== undefined) {
        form.append("templateId", input.templateId);
      }
      // Passing FormData tells the browser to set the multipart
      // Content-Type with boundary itself; useApi must not stringify it.
      return api<Review>("/reviews", { method: "POST", body: form });
    },
    onSuccess: (review) => {
      qc.setQueryData([...REVIEWS_KEY, review.id], review);
    },
  });
}

// [S-372] Progress events streamed by POST /reviews/stream (mirrors core's
// ReviewAgentEvent). The web turns these into human-readable progress lines.
export type ReviewAgentEvent =
  | { type: "tool_call"; tool: string; args?: unknown }
  | { type: "tool_result"; tool: string; ok: boolean }
  | { type: "analyzing" }
  | { type: "done"; deviationCount: number };

export type RunReviewStreamHandlers = {
  onEvent: (event: ReviewAgentEvent) => void;
  onReview: (review: Review) => void;
  onError: (error: { code?: string; message?: string }) => void;
};

/**
 * [S-372] Streaming review: POSTs to /reviews/stream and dispatches each NDJSON
 * line (progress event, final review, or error) to the handlers. Caches the
 * final review under [reviews, id] so the report screen renders without a
 * refetch.
 */
export function useRunReviewStream(): (
  input: RunReviewInput,
  handlers: RunReviewStreamHandlers
) => Promise<void> {
  const stream = useApiStream("admin");
  const qc = useQueryClient();
  return useCallback(
    async (input, handlers) => {
      const form = new FormData();
      form.append("file", input.file, input.file.name);
      form.append("dealType", input.dealType);
      form.append("guild", input.guild);
      if (input.templateId !== undefined) {
        form.append("templateId", input.templateId);
      }
      await stream("/reviews/stream", { body: form }, (line) => {
        let obj: Record<string, unknown>;
        try {
          obj = JSON.parse(line) as Record<string, unknown>;
        } catch {
          return; // skip a partial/garbled line
        }
        if (obj["event"] !== undefined) {
          handlers.onEvent(obj["event"] as ReviewAgentEvent);
        } else if (obj["review"] !== undefined) {
          const review = obj["review"] as Review;
          qc.setQueryData([...REVIEWS_KEY, review.id], review);
          handlers.onReview(review);
        } else if (obj["error"] !== undefined) {
          handlers.onError(obj["error"] as { code?: string; message?: string });
        }
      });
    },
    [stream, qc]
  );
}

export function useReview(reviewId: string | undefined): UseQueryResult<Review, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...REVIEWS_KEY, reviewId] as const,
    enabled: reviewId !== undefined && reviewId.length > 0,
    queryFn: () =>
      api<Review>(`/reviews/${encodeURIComponent(reviewId!)}`),
    // [S-766] A chat-started review is persisted as `building` and finished
    // after the reply; poll until it lands (complete / failed are terminal).
    refetchInterval: (query) =>
      query.state.data?.status === "building" ? 2500 : false,
  });
}

export type SubmitOverrideInput = {
  reviewId: string;
  findingId: string;
  reason: string;
};

export type SubmitOverrideOutput = {
  accepted: true;
  reviewId: string;
  findingId: string;
};

export function useSubmitOverride(): UseMutationResult<
  SubmitOverrideOutput,
  Error,
  SubmitOverrideInput
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: (input) =>
      api<SubmitOverrideOutput>("/audit/attorney-override", {
        method: "POST",
        body: input,
      }),
  });
}
